import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { FormKeys } from '@nx-customer-apps/shared/enums';
import { FormOptions } from '@nx-customer-apps/shared/interfaces';
import { TextboxQuestion } from '@nx-customer-apps/shared/utils';
import { FormToolsService } from '../../../../services/form-tools.service';
import { ValidatorsService } from '../../../../services/validators.service';
import { RollOutPolicy } from '../../../../policies';

@Injectable({
    providedIn: 'root'
})
export class EnergyCertificateFormService {
    constructor(
        private formToolsService: FormToolsService,
        private validatorsService: ValidatorsService,
        private rollOutPolicy: RollOutPolicy
    ) {}

    /*
     * Returns questions for Energy Certificate form.
     */
    public getEnergyCertificateQuestions(options?: FormOptions): TextboxQuestion[] {
        const questionsClone: TextboxQuestion[] = this.energyCertificate(options);
        return questionsClone.map(question => {
            const value = options?.questions?.[question.key]?.value;
            if (value) {
                question.value = value;
            }

            return question;
        });
    }

    private energyCertificate(options?: FormOptions): TextboxQuestion[] {
        const enclosureArea = new TextboxQuestion({
            question: 'HEATLOAD.ENERGY_CERTIFICATE.ENCLOSURE_AREA.TITLE',
            description: 'HEATLOAD.ENERGY_CERTIFICATE.ENCLOSURE_AREA.DESCRIPTION',
            defaultErrorMessage: 'HEATLOAD.ENERGY_CERTIFICATE.ENCLOSURE_AREA.ERRORS.REQUIRED',
            label: 'HEATLOAD.ENERGY_CERTIFICATE.ENCLOSURE_AREA.LABEL',
            key: FormKeys.SurroundingArea,
            mask: this.formToolsService.getNumericMask({ separatorLimit: '10000', numberOfDecimals: '2' }),
            hints: [this.formToolsService.getNumericHint()],
            validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 200, max: 20000 })],
            suffix: 'MEASUREMENT_UNITS.M2',
            value: options?.questions?.[FormKeys.SurroundingArea]?.value || ''
        });

        const buildingVolume = new TextboxQuestion({
            question: 'HEATLOAD.ENERGY_CERTIFICATE.BUILDING_VOLUME.TITLE',
            description: 'HEATLOAD.ENERGY_CERTIFICATE.BUILDING_VOLUME.DESCRIPTION',
            defaultErrorMessage: 'HEATLOAD.ENERGY_CERTIFICATE.BUILDING_VOLUME.ERRORS.REQUIRED',
            label: 'HEATLOAD.ENERGY_CERTIFICATE.BUILDING_VOLUME.LABEL',
            key: FormKeys.BuildingVolume,
            mask: this.formToolsService.getNumericMask({ separatorLimit: '10000', numberOfDecimals: '2' }),
            hints: [this.formToolsService.getNumericHint()],
            validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 200, max: 20000 })],
            suffix: 'MEASUREMENT_UNITS.M3',
            value: options?.questions?.[FormKeys.BuildingVolume]?.value || ''
        });

        if (!options?.countryCode) {
            throw Error('Please specify countryCode for air exchange rate question.');
        }

        const defaultAirExchangeRate = this.rollOutPolicy.getDefaultAirExchangeRate(options.countryCode);
        const airExchangeRate = new TextboxQuestion({
            question: 'HEATLOAD.ENERGY_CERTIFICATE.AIR_EXCH_RATE.TITLE',
            description: 'HEATLOAD.ENERGY_CERTIFICATE.AIR_EXCH_RATE.DESCRIPTION',
            defaultErrorMessage: 'HEATLOAD.ENERGY_CERTIFICATE.AIR_EXCH_RATE.ERRORS.REQUIRED',
            label: 'HEATLOAD.ENERGY_CERTIFICATE.AIR_EXCH_RATE.LABEL',
            key: FormKeys.AirExchangeRate,
            mask: this.formToolsService.getNumericMask({ separatorLimit: '10', numberOfDecimals: '2' }),
            hints: [this.formToolsService.getNumericHint()],
            validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 0.1, max: 10 })],
            suffix: 'MEASUREMENT_UNITS.1_H',
            tooltip: defaultAirExchangeRate ? 'HEATLOAD.ENERGY_CERTIFICATE.AIR_EXCH_RATE.TOOLTIP' : '',
            value: defaultAirExchangeRate || options?.questions?.[FormKeys.AirExchangeRate]?.value || ''
        });

        const heatLoss = new TextboxQuestion({
            question: 'HEATLOAD.ENERGY_CERTIFICATE.HEAT_LOSS.TITLE',
            description: 'HEATLOAD.ENERGY_CERTIFICATE.HEAT_LOSS.DESCRIPTION',
            defaultErrorMessage: 'HEATLOAD.ENERGY_CERTIFICATE.HEAT_LOSS.ERRORS.REQUIRED',
            label: 'HEATLOAD.ENERGY_CERTIFICATE.HEAT_LOSS.LABEL',
            mask: this.formToolsService.getNumericMask({ separatorLimit: '1', numberOfDecimals: '2' }),
            hints: [this.formToolsService.getNumericHint()],
            key: FormKeys.HeatLossTransmission,
            validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 0.05, max: 5 })],
            value: options?.questions?.[FormKeys.HeatLossTransmission]?.value || ''
        });

        return [enclosureArea, buildingVolume, airExchangeRate, heatLoss];
    }
}
